import qs from 'qs';
import { useEffect, useRef, useMemo } from 'react';
import DocumentTitle from 'react-document-title';
import { connect } from 'react-redux';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';

import { withTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import withDefaultAppContexts from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { getPageTitle } from 'hb-react/frontoffice/utils/page';
import * as FollowingsActions from 'hb-react/shared/apps/Topics/actions/followings';
import * as PostsActions from 'hb-react/shared/apps/Topics/actions/posts';
import * as TopicsActions from 'hb-react/shared/apps/Topics/actions/topics';
import TopicHeaderV2 from 'hb-react/shared/apps/Topics/components/TopicHeaderV2';
import {
  currentPageDisplayedPublicly,
  dynamicTopicResourcePath,
  redirectFromPublicRoot,
} from 'hb-react/shared/apps/Topics/utils';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import * as mobileDeviceFromUserAgent from 'helpers/mobileDeviceFromUserAgent';

import { topicRoutes } from './utils/routesUtils';

const Header = ({
  currentUser,
  topic,
  flags,
  followings,
  following,
  currentNetwork,
  socialLinks,
  topicsActions,
  topicMenuRef,
  unfollowTopic,
  updateSettings,
  infoSectionVisibility,
}) => {
  /*
   * Only apply for any connected user.
   * Group admin can disable the Group Live Feed.
   */
  if (currentUser) {
    const appPages = topic.app_pages ?? [];
    const liveFeedTabEnabled = topic.config?.live_feed_tab_enabled;

    topic.app_pages = appPages.filter((item) => {
      if (item.menu.feature_name === 'feed') {
        return liveFeedTabEnabled;
      }

      return true;
    });
  }

  return (
    <Routes>
      {topicRoutes({ flags }).map(({ path }) => (
        <Route
          path={path}
          key={path}
          element={
            <TopicHeaderV2
              members={followings}
              {...{
                following,
                currentNetwork,
                currentUser,
                socialLinks,
                topic,
                topicsActions,
                topicMenuRef,
                unfollowTopic,
                updateSettings,
                infoSectionVisibility,
              }}
            />
          }
        />
      ))}
    </Routes>
  );
};

const TopicRoutes = ({ flags, subdomainNetworkUrl, topic }) => {
  const topicRoutesArray = useMemo(
    () =>
      topicRoutes({ flags }).map(
        ({
          path,
          component: Component,
          Wrapper = ({ children }) => children,
        }) => (
          <Route
            path={`${path}`}
            key={path}
            element={
              <Wrapper>
                <Component
                  flags={flags}
                  topic={topic}
                  subdomainNetworkUrl={subdomainNetworkUrl}
                />
              </Wrapper>
            }
          />
        ),
      ),
    [topic],
  );

  return <Routes>{topicRoutesArray}</Routes>;
};

const TopicShowContainer = ({
  currentNetwork,
  currentUser,
  following,
  followings,
  socialLinks,
  subdomainNetworkUrl,
  topic,
  topicId,
  topicsActions,
  whitelabelURL,
  topicMenuRef,
  posts_actions: postActions,
  followingsActions,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const flags = useFeatureFlag();

  const prevTopic = useRef({ topicId }).current;

  useEffect(() => {
    postActions.initPosts();
    topicsActions.fetchTopicIfNeeded(topicId, { whitelabelURL });
  }, []);

  useEffect(() => {
    if (prevTopic.topicId !== topicId) {
      topicsActions.fetchTopicIfNeeded(topicId, { whitelabelURL });
    }

    return () => {
      prevTopic.topicId = topicId;
    };
  }, [topicId]);

  const infoSectionVisibility = (e) => {
    if (mobileDeviceFromUserAgent.any()) {
      // nodeName check to exclude event generated by dropdown
      if (!e.target || e.target.nodeName !== 'A') {
        const urlParams = qs.parse(window.location.search, {
          ignoreQueryPrefix: true,
        });

        const url = Routes.topic_feed_path(topicId);

        navigate(
          `${
            whitelabelURL ? url.replace(Routes.topic_path(topicId), '') : url
          }${urlParams.info ? '' : '?info=true'}`,
        );
      }
    }
  };

  const unfollowTopic = () =>
    followingsActions.unfollowTopic(topicId, following);

  const updateSettings = (following, params) =>
    followingsActions.updateSettings(topicId, following, params);

  if (!topic) {
    return <Loader size={50} />;
  }

  if (redirectFromPublicRoot(pathname, whitelabelURL, topic)) {
    return (
      <Navigate
        to={dynamicTopicResourcePath(topic.landing_page, {
          whitelabelURL,
          topicId,
        })}
      />
    );
  }

  if (
    !currentUser &&
    !currentPageDisplayedPublicly(pathname, whitelabelURL, topic)
  ) {
    return null;
  }

  return (
    <>
      {!flags.flagAnalyticsChangeTitleTags && (
        <DocumentTitle title={getPageTitle({ title: topic.name })} />
      )}
      <Header
        currentUser={currentUser}
        topic={topic}
        flags={flags}
        followings={followings}
        following={following}
        currentNetwork={currentNetwork}
        socialLinks={socialLinks}
        topicsActions={topicsActions}
        topicMenuRef={topicMenuRef}
        unfollowTopic={unfollowTopic}
        updateSettings={updateSettings}
        infoSectionVisibility={infoSectionVisibility}
      />
      <Box ml={0} pb={40} display="block">
        <TopicRoutes
          flags={flags}
          subdomainNetworkUrl={subdomainNetworkUrl}
          topic={topic}
        />
      </Box>
    </>
  );
};

const mapStateToProps = (state, props) => {
  const { currentUser, topicId } = props;

  const topic = state.topics.getIn(['topics', topicId.toString()]);

  const following = state.followings.get('followings').find(
    (following) =>
      following.get('topic_id') == topicId && // eslint-disable-line eqeqeq
      following.get('user_id') == currentUser?.id, // eslint-disable-line eqeqeq
  );

  return {
    following: following ? following.toJS() : null,
    followings: state.followings,
    socialLinks: state.social_links,
    topic: topic ? topic.toJS() : null,
    topicId,
  };
};

const mapDispatchToProps = (dispatch) => ({
  followingsActions: bindActionCreators(FollowingsActions, dispatch),
  topicsActions: bindActionCreators(TopicsActions, dispatch),
  posts_actions: bindActionCreators(PostsActions, dispatch),
});

export default compose(
  withDefaultAppContexts,
  withTopicContext,
  connect(mapStateToProps, mapDispatchToProps),
)(TopicShowContainer);
